// Money input — types digits, sees "250.000 ₫", stores raw number.
// Hỗ trợ gõ Tiếng Việt (Telex/VNI/Unikey/EVKey) & Tiếng Anh: chống nuốt chữ, chống nhảy số, hỗ trợ gõ tắt k/tr/nghìn/củ và hiển thị bằng chữ thông minh.

import * as React from 'react'
import { cn } from '@/lib/utils'
import { num, money, parseMoney, moneyInWords } from '@/lib/format'

export interface MoneyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: number | null | undefined
  onValueChange: (v: number) => void
  suffix?: string
  showWords?: boolean
  showQuickChips?: boolean
  quickChips?: string[]
  autoSelect?: boolean
}

function calculateDigitsBeforeCursor(str: string, pos: number): number {
  return str.slice(0, pos).replace(/\D/g, '').length
}

function calculateCursorPosition(formatted: string, targetDigits: number): number {
  if (targetDigits <= 0) return 0
  let count = 0
  for (let i = 0; i < formatted.length; i++) {
    const ch = formatted[i]
    if (ch && /\d/.test(ch)) {
      count++
      if (count === targetDigits) return i + 1
    }
  }
  return formatted.length
}

function getSmartSuggestions(text: string, fallbackChips?: string[]): number[] {
  const trimmed = text.trim().toLowerCase()
  if (!trimmed) {
    if (fallbackChips && fallbackChips.length > 0) {
      return fallbackChips.map((c) => parseMoney(c)).filter((n) => n > 0)
    }
    return [50_000, 100_000, 200_000, 500_000]
  }

  // Lấy các chữ số mà người dùng đang gõ
  const numMatch = trimmed.match(/^[\d.,]+/)
  if (!numMatch) {
    if (fallbackChips && fallbackChips.length > 0) {
      return fallbackChips.map((c) => parseMoney(c)).filter((n) => n > 0)
    }
    return [50_000, 100_000, 200_000, 500_000]
  }

  const rawDigits = numMatch[0].replace(/[^\d]/g, '')
  if (!rawDigits) {
    return [50_000, 100_000, 200_000, 500_000]
  }

  const n = parseInt(rawDigits, 10)
  if (n <= 0) {
    return [50_000, 100_000, 200_000, 500_000]
  }

  // Nếu người dùng đã gõ đầy đủ số tiền lớn (>= 100.000), ẩn gợi ý để tránh vướng
  if (n >= 100_000) {
    return []
  }

  const suggestions: number[] = []

  if (n < 10) {
    // Gõ "1", "2", "5" -> ví dụ "5" -> 50.000, 500.000, 5.000.000
    suggestions.push(n * 10_000)
    suggestions.push(n * 100_000)
    suggestions.push(n * 1_000_000)
  } else if (n < 100) {
    // Gõ "12", "15", "25", "99" -> ví dụ "15" -> 15.000, 150.000, 1.500.000
    suggestions.push(n * 1_000)
    suggestions.push(n * 10_000)
    suggestions.push(n * 100_000)
  } else if (n < 1_000) {
    // Gõ "120", "150", "350" -> ví dụ "150" -> 150.000, 1.500.000
    suggestions.push(n * 1_000)
    suggestions.push(n * 10_000)
  } else if (n < 10_000) {
    // Gõ "1500" -> 1.500.000
    suggestions.push(n * 1_000)
  }

  return Array.from(new Set(suggestions)).filter((v) => v > 0 && v < 1_000_000_000)
}

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  (
    {
      value,
      onValueChange,
      suffix = '₫',
      showWords = false,
      showQuickChips = false,
      quickChips = ['50k', '100k', '200k', '500k'],
      autoSelect = true,
      className,
      placeholder = '0',
      onFocus,
      onBlur,
      onKeyDown,
      ...props
    },
    forwardedRef,
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const isComposingRef = React.useRef(false)
    const isFocusedRef = React.useRef(false)
    const pendingCursorRef = React.useRef<number | null>(null)

    const [isFocused, setIsFocused] = React.useState(false)

    // Kết hợp forwardedRef và inputRef nội bộ
    React.useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement)

    const [text, setText] = React.useState<string>(() => (value != null && value > 0 ? num(value) : ''))

    // Đồng bộ vị trí con trỏ đồng bộ bằng useLayoutEffect (loại bỏ độ trễ 16ms của requestAnimationFrame)
    React.useLayoutEffect(() => {
      if (pendingCursorRef.current !== null && inputRef.current) {
        const pos = pendingCursorRef.current
        inputRef.current.setSelectionRange(pos, pos)
        pendingCursorRef.current = null
      }
    })

    // Đồng bộ từ props bên ngoài (khi reset form hoặc đổi giá trị)
    React.useEffect(() => {
      if (isFocusedRef.current && document.activeElement === inputRef.current) {
        return
      }
      const parsed = parseMoney(text)
      if (parsed !== (value ?? 0)) {
        setText(value != null && value > 0 ? num(value) : '')
      }
    }, [value])

    const processInput = React.useCallback(
      (raw: string, cursorPosInRaw?: number) => {
        if (!raw.trim()) {
          setText('')
          onValueChange(0)
          pendingCursorRef.current = 0
          return
        }

        const cursorPos = cursorPosInRaw ?? raw.length
        const digitsBefore = calculateDigitsBeforeCursor(raw, cursorPos)

        // Kiểm tra xem chuỗi có chứa ký tự gõ tắt (chữ cái) không
        const hasLetters = /[a-zA-Zà-ỹÀ-Ỹ]/.test(raw)

        if (hasLetters) {
          // Đang gõ chữ (ví dụ "1tr5", "120k", "50 nghìn"): giữ nguyên text để người dùng gõ tiếp
          const parsed = parseMoney(raw)
          setText(raw)
          onValueChange(parsed)
          pendingCursorRef.current = cursorPos
          return
        }

        // Chuỗi chỉ gồm số và dấu phân cách: format dấu chấm phân cách hàng nghìn
        const cleanDigits = raw.replace(/\D/g, '')
        const parsed = cleanDigits ? Number(cleanDigits) : 0
        const formatted = parsed > 0 ? num(parsed) : cleanDigits ? '0' : ''

        setText(formatted)
        onValueChange(parsed)

        // Tính vị trí con trỏ mới trong chuỗi đã được định dạng
        const newPos = calculateCursorPosition(formatted, digitsBefore)
        pendingCursorRef.current = newPos
      },
      [onValueChange],
    )

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      if (isComposingRef.current) {
        setText(e.target.value)
        return
      }
      processInput(e.target.value, e.target.selectionStart ?? undefined)
    }

    function handleCompositionStart() {
      isComposingRef.current = true
    }

    function handleCompositionEnd(e: React.CompositionEvent<HTMLInputElement>) {
      isComposingRef.current = false
      processInput(e.currentTarget.value, e.currentTarget.selectionStart ?? undefined)
    }

    function handleKeyDownEvent(e: React.KeyboardEvent<HTMLInputElement>) {
      // Xử lý phím Backspace thông minh: khi đứng sau dấu chấm '.', xóa luôn số phía trước dấu chấm
      if (e.key === 'Backspace') {
        const el = inputRef.current
        if (el && el.selectionStart === el.selectionEnd && el.selectionStart && el.selectionStart > 0) {
          const pos = el.selectionStart
          const ch = el.value[pos - 1]
          if (ch === '.' || ch === ',') {
            e.preventDefault()
            const before = el.value.slice(0, pos - 2)
            const after = el.value.slice(pos)
            const newRaw = before + after
            processInput(newRaw, Math.max(0, pos - 2))
            return
          }
        }
      }

      // Nhấn Enter để chốt giá trị đẹp
      if (e.key === 'Enter') {
        const parsed = parseMoney(text)
        setText(parsed > 0 ? num(parsed) : '')
        onValueChange(parsed)
      }

      onKeyDown?.(e)
    }

    function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
      isFocusedRef.current = true
      setIsFocused(true)
      if (autoSelect) {
        // Tự động bôi đen toàn bộ để khi gõ số mới, giá trị cũ lập tức bị thay thế
        e.target.select()
      }
      onFocus?.(e)
    }

    function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
      isFocusedRef.current = false
      setIsFocused(false)
      const parsed = parseMoney(text)
      setText(parsed > 0 ? num(parsed) : value && value > 0 ? num(value) : '')
      onValueChange(parsed)
      onBlur?.(e)
    }

    function applySuggestion(amount: number) {
      const formatted = num(amount)
      setText(formatted)
      onValueChange(amount)
      pendingCursorRef.current = formatted.length
      inputRef.current?.focus()
    }

    const currentParsed = parseMoney(text) || (value ?? 0)
    const dynamicSuggestions = React.useMemo(() => getSmartSuggestions(text, quickChips), [text, quickChips])

    return (
      <div className="relative w-full">
        <div className="relative">
          <input
            ref={inputRef}
            data-slot="input"
            type="text"
            inputMode="numeric"
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDownEvent}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={cn(
              'flex h-11 w-full rounded-md border border-rule bg-raise px-3.5 py-2 pr-8 text-base sm:text-sm text-ink text-right tabular-nums',
              'placeholder:text-muted',
              'transition-shadow',
              'focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/20',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'aria-invalid:border-danger aria-invalid:ring-2 aria-invalid:ring-danger/20',
              className,
            )}
            {...props}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted tabular-nums">
            {suffix}
          </span>
        </div>

        {/* Hiển thị số tiền thông minh & đọc thành chữ */}
        {showWords && currentParsed > 0 && (
          <div className="mt-1.5 flex items-start gap-1.5 text-xs text-accent font-medium leading-relaxed transition-all">
            <div className="flex-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="font-semibold font-mono text-accent">{money(currentParsed)}</span>
              <span className="text-muted">•</span>
              <span className="text-inkSoft italic">{moneyInWords(currentParsed)}</span>
            </div>
          </div>
        )}

        {/* Nút bấm gợi ý số tiền động theo số người dùng đang gõ */}
        {showQuickChips && isFocused && dynamicSuggestions.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5 items-center animate-in fade-in duration-150">
            <span className="text-xs text-muted font-medium mr-0.5">Gợi ý:</span>
            {dynamicSuggestions.map((amount) => (
              <button
                key={amount}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault() // Tránh mất focus khỏi ô input
                  applySuggestion(amount)
                }}
                className="px-2 py-0.5 text-xs font-semibold rounded-md border border-accent/25 bg-accent/5 hover:bg-accent hover:text-white hover:border-accent text-accent transition-all tabular-nums cursor-pointer"
              >
                {money(amount)}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  },
)
MoneyInput.displayName = 'MoneyInput'
